import { Download, Phone, PhoneOff } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Callout, EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Toolbar } from "@/components/layout/page";
import { CallPager, CallRangeLabel } from "@/features/call-report";
import { ApiError, fetchFile, qs, saveBlob } from "@/lib/api";
import { DEFAULT_RANGE, RANGES, rangeSince, type Range } from "@/lib/calls";
import { carrierSaid, checkNotice, followUpText, lostCallWho } from "@/lib/lost-calls";
import { callBackPath, callBackTitle } from "@/lib/phone-page";
import { isDialable } from "@/lib/phone-number";
import { CALLS_PAGE_SIZE, useLostCalls, type LostCallFilters } from "@/lib/queries";
import type { LostCall } from "@/lib/types";
import { formatDateTime, formatPhoneNumber } from "@/lib/utils";
import { canDialSecondLine, dialBlockedReason } from "@/softphone/call-control";
import { useOutboundDial } from "@/softphone/manual-call";
import { useSoftphone } from "@/softphone/softphone-context";

/**
 * The Calls page's "Never reached us" (platform ADR-178): inbound calls the carrier sent and our
 * switch never took, or that the carrier cut off within two seconds.
 *
 * Every other list on the page is built from what the switch reported, and a switch that is down
 * reports nothing. These rows come from the carrier's own log, read every few minutes, so this is
 * where a night like 21 Oct shows up: who called, when, which of our numbers, and what the carrier
 * says happened. The line over the table says whether that log is being read at all, since an
 * empty list where the check is off is not "nothing lost".
 *
 * Call back goes out the way the call came in (`callBackPath`, as the phone's missed calls do):
 * through the campaign the number routes to, which the person calling back has to be Ready on, or
 * a plain call from the phone for a rep's own number.
 */
export function LostCallsView() {
  const toast = useToast();
  const [range, setRange] = React.useState<Range>(DEFAULT_RANGE);
  const [page, setPage] = React.useState(0);
  const [exporting, setExporting] = React.useState(false);
  // "All time" is a date before any call; the server's own default is the last week.
  const since = React.useMemo(() => rangeSince(range) ?? new Date(0).toISOString(), [range]);
  const filters: LostCallFilters = React.useMemo(() => ({ since }), [since]);
  React.useEffect(() => setPage(0), [filters]);
  const list = useLostCalls(filters, page);

  const phone = useSoftphone();
  const out = useOutboundDial();
  const blocked = dialBlockedReason(phone.registration, canDialSecondLine(phone.calls));
  const [placing, setPlacing] = React.useState<number | null>(null);
  const [refusal, setRefusal] = React.useState<string | null>(null);

  async function exportCsv() {
    setExporting(true);
    try {
      const { blob, headers } = await fetchFile(`/lost-calls/export.csv${qs(filters)}`);
      saveBlob(blob, `never-reached-us-${new Date().toISOString().slice(0, 10)}.csv`);
      if (headers.get("X-Export-Truncated") === "true") {
        toast.info("Export truncated", "The file hit the row cap. Narrow the date range.");
      } else {
        toast.success("Export downloaded");
      }
    } catch (error) {
      toast.error(
        "Could not export",
        error instanceof ApiError ? error.message : "The download did not start.",
      );
    } finally {
      setExporting(false);
    }
  }

  async function callBack(call: LostCall) {
    setRefusal(null);
    const back = callBackPath(
      { campaign_id: call.campaign_id, campaign_name: call.campaign_name, queue_name: null },
      out.path(call.caller_number),
    );
    if (back.via === "blocked") {
      setRefusal(back.reason);
      return;
    }
    setPlacing(call.id);
    try {
      await out.place(call.caller_number, back.via === "campaign" ? back.campaign.campaign_id : null);
    } catch (e) {
      if (e instanceof ApiError) setRefusal(e.message);
    } finally {
      setPlacing(null);
    }
  }

  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? null;
  const notice = list.data ? checkNotice(list.data.check, Date.now(), formatDateTime) : null;

  return (
    <>
      {notice && (
        notice.tone === "warning" ? (
          <Callout tone="warning" className="mb-4">{notice.text}</Callout>
        ) : (
          <p className="mb-4 text-xs text-text-muted">{notice.text}</p>
        )
      )}
      <Card className="overflow-hidden">
        <Toolbar>
          <Select value={range} onValueChange={(v) => setRange(v as Range)}>
            <SelectTrigger className="w-40" aria-label="Time range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(RANGES).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => void exportCsv()} disabled={exporting}>
            {exporting ? <Spinner /> : <Download />}
            Export CSV
          </Button>
          <span className="ml-auto flex items-center gap-2 text-xs text-text-muted">
            {list.isFetching && !list.isPending && <Spinner className="size-3" />}
            <CallRangeLabel from={page * CALLS_PAGE_SIZE} count={rows.length} total={total} />
          </span>
        </Toolbar>

        {refusal && (
          <Callout tone="warning" className="mx-3 mt-3">{refusal}</Callout>
        )}

        {list.isPending ? (
          <TableSkeleton cols={6} rows={6} />
        ) : list.error ? (
          <ErrorState
            error={list.error}
            title="Could not load the calls that never reached us"
            action={
              <Button variant="outline" onClick={() => void list.refetch()}>
                Try again
              </Button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={PhoneOff}
            title="No lost calls in this range"
            description="A call the carrier sent us that never reached the switch shows up here a few minutes after it."
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Caller</TableHead>
                  <TableHead>Our number</TableHead>
                  <TableHead>What the carrier says</TableHead>
                  <TableHead>Since</TableHead>
                  <TableHead className="w-12 text-right">
                    <span className="sr-only">Call back</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((call) => (
                  <LostCallRow
                    key={call.id}
                    call={call}
                    blocked={blocked}
                    placing={placing}
                    onCallBack={() => void callBack(call)}
                    title={
                      blocked ??
                      callBackTitle(
                        callBackPath(
                          { campaign_id: call.campaign_id, campaign_name: call.campaign_name, queue_name: null },
                          out.path(call.caller_number),
                        ),
                      )
                    }
                  />
                ))}
              </TableBody>
            </Table>
            <CallPager
              page={page}
              count={rows.length}
              total={total}
              onChange={setPage}
              busy={list.isFetching}
            />
          </>
        )}
      </Card>
    </>
  );
}

function LostCallRow({
  call, blocked, placing, onCallBack, title,
}: {
  call: LostCall;
  blocked: string | null;
  placing: number | null;
  onCallBack: () => void;
  title: string;
}) {
  const said = carrierSaid(call);
  const since = followUpText(call, formatDateTime);
  const who = lostCallWho(call, formatPhoneNumber);
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap text-xs text-text-muted">
        {formatDateTime(call.at)}
      </TableCell>
      <MonoCell className="whitespace-nowrap">
        <span className="flex flex-col leading-tight">
          {call.lead_id ? (
            <Link to={`/contacts/${call.lead_id}`} className="font-sans text-text hover:underline">
              {who}
            </Link>
          ) : (
            <span className={call.lead_name ? "font-sans text-text" : "text-text"}>{who}</span>
          )}
          {call.lead_name && !call.hidden && (
            <span className="text-2xs text-text-muted">{formatPhoneNumber(call.caller_number)}</span>
          )}
        </span>
      </MonoCell>
      <MonoCell className="whitespace-nowrap">
        <span className="flex flex-col leading-tight">
          <span className="text-text">{formatPhoneNumber(call.our_number)}</span>
          {call.line_name && (
            <span className="font-sans text-2xs text-text-muted">{call.line_name}</span>
          )}
        </span>
      </MonoCell>
      <TableCell>
        <span className="flex flex-col items-start gap-0.5">
          <Badge tone={said.tone} size="sm">{said.label}</Badge>
          <span className="text-2xs text-text-muted">{said.detail}</span>
        </span>
      </TableCell>
      <TableCell className={since.done ? "text-xs text-text" : "text-xs text-text-muted"}>
        {since.text}
      </TableCell>
      <TableCell className="text-right">
        {!call.hidden && isDialable(call.caller_number) ? (
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={`Call ${who} back`}
            title={title}
            disabled={blocked !== null || (placing !== null && placing !== call.id)}
            loading={placing === call.id}
            onClick={onCallBack}
          >
            <Phone />
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}
